import echarts from "echarts"
import "echarts/extension/bmap/bmap"
import { PieChartColor } from "@/config"

/** 现代商务风图表通用色与交互样式 */
const CHART_THEME = {
  axisLabel: "#94A3B8",
  axisLine: "#E2E8F0",
  splitLine: "#F1F5F9",
  textPrimary: "#1E293B",
  textSecondary: "#64748B",
  barGradient: ["#93C5FD", "#2563EB"],
  line: "#3B82F6",
  areaTop: "rgba(59, 130, 246, 0.22)",
  areaBottom: "rgba(59, 130, 246, 0.02)"
}

function buildModernTooltip (extra = {}) {
  return {
    backgroundColor: "rgba(15, 23, 42, 0.92)",
    borderWidth: 0,
    borderColor: "transparent",
    padding: [10, 14],
    textStyle: {
      color: "#F8FAFC",
      fontSize: 13,
      lineHeight: 20
    },
    extraCssText:
      "border-radius: 10px; box-shadow: 0 12px 28px rgba(15, 23, 42, 0.22); backdrop-filter: blur(6px);",
    ...extra
  }
}

function buildPieLegend (chartData) {
  const dataLength = chartData.length
  const many = dataLength > 5
  const valueMap = {}
  chartData.forEach(item => {
    valueMap[item.name] = item.value
  })

  return {
    show: true,
    type: many ? "scroll" : "plain",
    orient: "horizontal",
    left: "center",
    bottom: 8,
    width: "96%",
    height: many ? 64 : 48,
    data: chartData.map(item => item.name),
    itemWidth: 10,
    itemHeight: 10,
    itemGap: 12,
    icon: "circle",
    pageIconSize: 10,
    pageButtonItemGap: 6,
    pageButtonGap: 8,
    pageIconColor: "#3B82F6",
    pageIconInactiveColor: "#CBD5E1",
    pageTextStyle: {
      color: "#94A3B8",
      fontSize: 11
    },
    textStyle: {
      color: CHART_THEME.textSecondary,
      fontSize: 12,
      lineHeight: 16,
      padding: [2, 0, 2, 2]
    },
    formatter (name) {
      const value = valueMap[name]
      const shortName = name && name.length > 6 ? name.slice(0, 6) + "…" : name
      if (value == null) return shortName
      return `${shortName} ${value}`
    }
  }
}

export function drawGeneralStatisticsChart (ele, data) {
  const chartData = Array.isArray(data) ? data : []
  const total = chartData.reduce((sum, item) => sum + (Number(item.value) || 0), 0)
  const many = chartData.length > 5

  const option = {
    color: PieChartColor,
    animationDuration: 800,
    animationEasing: "cubicOut",
    tooltip: buildModernTooltip({
      trigger: "item",
      formatter (params) {
        const percent = params.percent != null ? params.percent : 0
        return [
          `<div style="font-weight:600;margin-bottom:4px;">${params.name}</div>`,
          `<div style="opacity:.9;">数量：<b>${params.value}</b></div>`,
          `<div style="opacity:.9;">占比：<b>${percent}%</b></div>`
        ].join("")
      }
    }),
    legend: buildPieLegend(chartData),
    graphic: [
      {
        id: "pie-center-total",
        type: "group",
        left: "center",
        top: many ? "32%" : "34%",
        bounding: "raw",
        silent: true,
        z: 100,
        children: [
          {
            type: "text",
            style: {
              text: String(total),
              textAlign: "center",
              textVerticalAlign: "middle",
              fill: CHART_THEME.textPrimary,
              fontSize: 26,
              fontWeight: 700,
              fontFamily: "Helvetica Neue, PingFang SC, Microsoft YaHei, sans-serif"
            },
            top: -12
          },
          {
            type: "text",
            style: {
              text: "总量",
              textAlign: "center",
              textVerticalAlign: "middle",
              fill: CHART_THEME.textSecondary,
              fontSize: 12,
              fontFamily: "Helvetica Neue, PingFang SC, Microsoft YaHei, sans-serif"
            },
            top: 14
          }
        ]
      }
    ],
    series: [
      {
        name: "数量占比",
        type: "pie",
        radius: many ? ["34%", "52%"] : ["36%", "56%"],
        center: ["50%", many ? "38%" : "40%"],
        avoidLabelOverlap: true,
        minAngle: 2,
        hoverOffset: 8,
        itemStyle: {
          normal: {
            borderColor: "#fff",
            borderWidth: 3,
            shadowBlur: 8,
            shadowOffsetY: 2,
            shadowColor: "rgba(15, 23, 42, 0.08)"
          },
          emphasis: {
            shadowBlur: 16,
            shadowColor: "rgba(37, 99, 235, 0.25)"
          }
        },
        label: {
          normal: {
            show: false,
            position: "center"
          },
          emphasis: {
            show: false
          }
        },
        labelLine: {
          normal: {
            show: false
          }
        },
        data: chartData
      }
    ]
  }
  const myChart = this.$echarts.init(this.$refs[ele])
  setTimeout(() => {
    myChart.resize({
      width: myChart._dom.clientWidth
    })
  })
  myChart.setOption(option, true)
}

export function chartsResize (ele) {
  if (!this.$refs[ele]) return
  const myChart = this.$echarts.init(this.$refs[ele])
  setTimeout(() => {
    myChart.resize({
      width: myChart._dom.clientWidth
    })
  })
}

export function drawColumnsAndTrendsChart (ele, data) {
  const that = this
  const list = Array.isArray(data) ? data : []
  const barGradient = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: CHART_THEME.barGradient[0] },
    { offset: 1, color: CHART_THEME.barGradient[1] }
  ])

  const option = {
    animationDuration: 900,
    animationEasing: "cubicOut",
    tooltip: buildModernTooltip({
      trigger: "axis",
      axisPointer: {
        type: "shadow",
        shadowStyle: {
          color: "rgba(59, 130, 246, 0.08)"
        }
      },
      formatter (params) {
        if (!params || !params.length) return ""
        const point = params[0]
        return [
          `<div style="font-weight:600;margin-bottom:4px;">${point.axisValue}</div>`,
          `<div style="opacity:.9;">数量：<b>${point.value}</b></div>`
        ].join("")
      }
    }),
    toolbox: {
      right: 8,
      top: 0,
      feature: {
        saveAsImage: {
          title: "保存图片",
          pixelRatio: 2,
          backgroundColor: "#fff"
        }
      },
      iconStyle: {
        borderColor: "#94A3B8"
      }
    },
    backgroundColor: "transparent",
    grid: {
      top: 36,
      right: 24,
      left: 16,
      bottom: 16,
      containLabel: true
    },
    xAxis: [
      {
        type: "category",
        data: list.map(item => item.dateNo),
        boundaryGap: true,
        axisLabel: {
          margin: 14,
          color: CHART_THEME.axisLabel,
          fontSize: 12
        },
        axisLine: {
          lineStyle: {
            color: CHART_THEME.axisLine
          }
        },
        axisTick: {
          show: false
        }
      }
    ],
    yAxis: [
      {
        type: "value",
        axisLabel: {
          color: CHART_THEME.axisLabel,
          fontSize: 12
        },
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        splitLine: {
          lineStyle: {
            color: CHART_THEME.splitLine,
            type: "dashed"
          }
        }
      }
    ],
    series: [
      {
        type: "bar",
        name: "数量",
        data: list.map(item => item.count),
        barWidth: 18,
        barMaxWidth: 28,
        itemStyle: {
          normal: {
            color: barGradient,
            barBorderRadius: [8, 8, 0, 0],
            shadowBlur: 8,
            shadowColor: "rgba(37, 99, 235, 0.18)",
            shadowOffsetY: 4
          },
          emphasis: {
            shadowBlur: 14,
            shadowColor: "rgba(37, 99, 235, 0.28)"
          }
        },
        label: {
          normal: {
            show: true,
            fontSize: 11,
            fontWeight: 500,
            color: CHART_THEME.textSecondary,
            position: "top",
            distance: 6
          }
        },
        z: 2
      },
      {
        type: "line",
        name: "趋势",
        data: list.map(item => item.count),
        smooth: true,
        symbol: "circle",
        symbolSize: 7,
        showSymbol: false,
        itemStyle: {
          normal: {
            color: "#fff",
            borderColor: CHART_THEME.line,
            borderWidth: 2
          }
        },
        lineStyle: {
          normal: {
            color: CHART_THEME.line,
            width: 3,
            shadowColor: "rgba(59, 130, 246, 0.35)",
            shadowBlur: 10,
            shadowOffsetY: 6
          }
        },
        areaStyle: {
          normal: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: CHART_THEME.areaTop },
              { offset: 1, color: CHART_THEME.areaBottom }
            ])
          }
        },
        z: 3
      }
    ]
  }

  const myChart = that.$echarts.init(this.$refs[ele])
  myChart.setOption(option, true)

  window.addEventListener("resize", () => {
    setTimeout(() => {
      myChart.resize({
        width: myChart._dom.clientWidth
      })
    })
  })
}

// export function drawDoubleColumnsAndTrendsChart(ele, data) {
//   const that = this
//   const option = {
//     tooltip: {
//       trigger: "axis"
//     },
//     toolbox: {
//       feature: {
//         saveAsImage: {}
//       }
//     },
//     backgroundColor: "#fff",
//     grid: {
//       top: "10%",
//       right: "10%",
//       left: "10%",
//       bottom: "10%",
//       containLabel: true
//     },
//     xAxis: [
//       {
//         type: "category",
//         color: "#AFAFAF",
//         data: [],
//         axisLabel: {
//           // formatter: "{value}月",
//           margin: 20,
//           color: "#DADADA",
//           textStyle: {
//             fontSize: 12
//           }
//         },
//         axisLine: {
//           lineStyle: {
//             color: "rgba(173,183,255,1)"
//           }
//         },
//         axisTick: {
//           show: false
//         }
//       }
//     ],
//     yAxis: [
//       {
//         axisLabel: {
//           // formatter: "{value}",
//           color: "#AFAFAF",
//           textStyle: {
//             fontSize: 12
//           }
//         },
//         axisLine: {
//           lineStyle: {
//             color: "rgba(107,107,107,0.37)"
//           }
//         },
//         axisTick: {
//           show: false
//         },
//         splitLine: {
//           lineStyle: {
//             color: "rgba(131,101,101,0.2)",
//             type: "dashed"
//           }
//         }
//       }
//     ],
//     series: [
//       {
//         type: "bar",
//         data: [],
//         barWidth: "20px",
//         itemStyle: {
//           normal: {
//             color: function() {
//               //展示正值的柱子，负数设为透明
//               let colorArr = ["#C3E8FF", "#112AF8"]
//               return new echarts.graphic.LinearGradient(
//                 0,
//                 0,
//                 0,
//                 1,
//                 [
//                   {
//                     offset: 0,
//                     color: colorArr[0] // 0% 处的颜色
//                   },
//                   {
//                     offset: 1,
//                     color: colorArr[1] // 100% 处的颜色
//                   }
//                 ],
//                 false
//               )
//             },
//             barBorderRadius: [30, 30, 0, 0]
//           }
//         },
//         label: {
//           normal: {
//             show: true,
//             fontSize: 12,
//             color: "#AFAFAF",
//             position: "top"
//           }
//         }
//       },
//       {
//         data: [],
//         type: "line",
//         smooth: true,
//         name: "合同数",
//         symbol: "none",
//         lineStyle: {
//           color: "#ADB7FF",
//           width: 4,
//           shadowColor: "rgba(0, 0, 0, 0.3)", //设置折线阴影
//           shadowBlur: 15,
//           shadowOffsetY: 20
//         }
//       },
//       {
//         type: "bar",
//         data: [],
//         barWidth: "20px",
//         itemStyle: {
//           normal: {
//             color: function() {
//               //展示正值的柱子，负数设为透明
//               let colorArr = ["#e1630a", "#ef9e08"]
//               return new echarts.graphic.LinearGradient(
//                 0,
//                 0,
//                 0,
//                 1,
//                 [
//                   {
//                     offset: 0,
//                     color: colorArr[0] // 0% 处的颜色
//                   },
//                   {
//                     offset: 1,
//                     color: colorArr[1] // 100% 处的颜色
//                   }
//                 ],
//                 false
//               )
//             },
//             barBorderRadius: [30, 30, 0, 0]
//           }
//         },
//         label: {
//           normal: {
//             show: true,
//             fontSize: 12,
//             color: "#AFAFAF",
//             position: "top"
//           }
//         }
//       },
//       {
//         data: [],
//         type: "line",
//         smooth: true,
//         name: "合同金额",
//         symbol: "none",
//         lineStyle: {
//           color: "#e87808",
//           width: 4,
//           shadowColor: "rgba(0, 0, 0, 0.3)", //设置折线阴影
//           shadowBlur: 15,
//           shadowOffsetY: 20
//         }
//       }
//     ]
//   }
//
//   option.xAxis[0].data = data.map(item => item.dateNo)
//   option.series[0].data = data.map(item => item.count)
//   option.series[1].data = data.map(item => item.count)
//   option.series[2].data = data.map(item => item.totalPrice)
//   option.series[3].data = data.map(item => item.totalPrice)
//   const myChart = that.$echarts.init(this.$refs[ele])
//   myChart.setOption(option)
// }

export function drawColumnsChart (ele, data, legendData, seriesName) {
  const that = this
  const barGradient = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: CHART_THEME.barGradient[0] },
    { offset: 1, color: CHART_THEME.barGradient[1] }
  ])
  const option = {
    tooltip: buildModernTooltip({
      trigger: "axis"
    }),
    grid: {
      top: 30,
      right: 5,
      left: 5,
      bottom: 40,
      containLabel: true
    },
    legend: {
      data: [...legendData],
      textStyle: {
        color: CHART_THEME.textSecondary
      }
    },
    toolbox: {
      show: false
    },
    calculable: true,
    xAxis: [
      {
        type: "category",
        axisLabel: {
          rotate: 45,
          color: CHART_THEME.axisLabel
        },
        axisLine: {
          lineStyle: {
            color: CHART_THEME.axisLine
          }
        },
        data: data.xAxis
      }
    ],
    yAxis: [
      {
        type: "value",
        axisLabel: {
          color: CHART_THEME.axisLabel
        },
        splitLine: {
          lineStyle: {
            color: CHART_THEME.splitLine,
            type: "dashed"
          }
        }
      }
    ],
    series: [
      {
        name: seriesName,
        type: "bar",
        data: data.series,
        markPoint: {
          data: data.markPoint
        },
        markLine: {
          data: [{ type: "average", name: "平均值" }]
        },
        barWidth: 30,
        itemStyle: {
          normal: {
            color: barGradient,
            barBorderRadius: [8, 8, 0, 0]
          }
        }
      }
    ],
    axisLabel: {
      interval: Math.ceil((data.xAxis || []).length / 24),
      show: true,
      textStyle: {
        color: "#a9a9a9",
        fontSize: 10
      },
      rotate: 40,
      formatter: function (v) {
        return v
      },
      showMinLabel: true,
      showMaxLabel: true
    }
  }
  const myChart = that.$echarts.init(this.$refs[ele])
  myChart.setOption(option, true)
}

export function drawFunnelChartTree (ele, data) {
  const that = this
  const option = {
    color: PieChartColor,
    tooltip: buildModernTooltip({}),
    toolbox: {
      feature: {
        dataView: { readOnly: false },
        restore: {},
        saveAsImage: {}
      }
    },
    legend: {
      textStyle: {
        color: CHART_THEME.textSecondary
      }
    },
    series: [
      {
        type: "funnel",
        left: "10%",
        top: 60,
        bottom: 60,
        width: "80%",
        min: 0,
        max: 100,
        minSize: "0%",
        maxSize: "100%",
        sort: "ascending",
        gap: 2,
        label: {
          show: true,
          position: "inside"
        },
        labelLine: {
          length: 10,
          lineStyle: {
            width: 1,
            type: "solid"
          }
        },
        itemStyle: {
          borderColor: "#fff",
          borderWidth: 1
        },
        emphasis: {
          label: {
            fontSize: 20
          }
        },
        data
      }
    ]
  }
  const myChart = that.$echarts.init(this.$refs[ele])
  myChart.setOption(option, true)
}
